import type { ReceiverWeeklyTarget } from "@/types/container";

// Mock data per target settimanali
const mockTargets: ReceiverWeeklyTarget[] = [
    // Target generico settimanale per receiver "2" (Global Partners Corp) - 2024
    {
        id: "target-1",
        receiverId: "2",
        year: 2024,
        // weekNumber undefined = target generico
        containerTarget: 5, // 5 container a settimana
        revenueTarget: 500000, // $500k a settimana
        createdAt: new Date("2024-01-01"),
        updatedAt: new Date("2024-01-01"),
    },
    // Target generico settimanale per receiver "2" - 2025
    {
        id: "target-2",
        receiverId: "2",
        year: 2025,
        containerTarget: 6,
        revenueTarget: 600000,
        createdAt: new Date("2025-01-01"),
        updatedAt: new Date("2025-01-01"),
    },
    // Target generico settimanale per receiver "2" - 2026
    {
        id: "target-3",
        receiverId: "2",
        year: 2026,
        containerTarget: 7,
        revenueTarget: 700000,
        createdAt: new Date("2026-01-01"),
        updatedAt: new Date("2026-01-01"),
    },
    // Target generico per altri receiver
    {
        id: "target-4",
        receiverId: "4",
        year: 2024,
        containerTarget: 3,
        revenueTarget: 300000,
        createdAt: new Date("2024-01-01"),
        updatedAt: new Date("2024-01-01"),
    },
    {
        id: "target-5",
        receiverId: "6",
        year: 2024,
        containerTarget: 4,
        revenueTarget: 400000,
        createdAt: new Date("2024-01-01"),
        updatedAt: new Date("2024-01-01"),
    },
    {
        id: "target-6",
        receiverId: "8",
        year: 2024,
        containerTarget: 2,
        revenueTarget: 200000,
        createdAt: new Date("2024-01-01"),
        updatedAt: new Date("2024-01-01"),
    },
    // Esempi di override per settimane specifiche
    {
        id: "target-7",
        receiverId: "2",
        year: 2024,
        weekNumber: 11, // Settimana 11
        containerTarget: 8, // Target aumentato per questa settimana
        revenueTarget: 800000,
        createdAt: new Date("2024-02-15"),
        updatedAt: new Date("2024-02-15"),
    },
    {
        id: "target-8",
        receiverId: "2",
        year: 2024,
        weekNumber: 25, // Settimana 25
        containerTarget: 10,
        revenueTarget: 1000000,
        createdAt: new Date("2024-05-20"),
        updatedAt: new Date("2024-05-20"),
    },
];

// In-memory storage
let targets = [...mockTargets];

/**
 * Service per gestire i target settimanali dei receiver
 */
export const receiverTargetService = {
    /**
     * Ottiene il target per una settimana specifica.
     * Se esiste un override per quella settimana, lo restituisce.
     * Altrimenti restituisce il target generico settimanale.
     */
    getTarget: (receiverId: string, year: number, weekNumber?: number): ReceiverWeeklyTarget | null => {
        // Prima cerca un override specifico per la settimana
        if (weekNumber !== undefined) {
            const specificTarget = targets.find(
                (t) => t.receiverId === receiverId && t.year === year && t.weekNumber === weekNumber
            );
            if (specificTarget) {
                return { ...specificTarget };
            }
        }

        // Altrimenti restituisce il target generico
        const genericTarget = targets.find(
            (t) => t.receiverId === receiverId && t.year === year && t.weekNumber === undefined
        );
        return genericTarget ? { ...genericTarget } : null;
    },

    /**
     * Ottiene tutti i target per un receiver in un anno (generico + override specifici)
     */
    getYearlyTargets: (receiverId: string, year: number): ReceiverWeeklyTarget[] => {
        return targets
            .filter((t) => t.receiverId === receiverId && t.year === year)
            .map((t) => ({ ...t }));
    },

    /**
     * Imposta o aggiorna un target per una settimana specifica.
     * Se weekNumber è undefined, aggiorna il target generico.
     */
    setTarget: (
        receiverId: string,
        year: number,
        containerTarget: number,
        revenueTarget: number,
        weekNumber?: number
    ): ReceiverWeeklyTarget => {
        const existing = targets.find(
            (t) => t.receiverId === receiverId && t.year === year && t.weekNumber === weekNumber
        );

        if (existing) {
            // Aggiorna target esistente
            existing.containerTarget = containerTarget;
            existing.revenueTarget = revenueTarget;
            existing.updatedAt = new Date();
            return { ...existing };
        } else {
            // Crea nuovo target
            const newTarget: ReceiverWeeklyTarget = {
                id: `target-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                receiverId,
                year,
                weekNumber,
                containerTarget,
                revenueTarget,
                createdAt: new Date(),
                updatedAt: new Date(),
            };
            targets.push(newTarget);
            return { ...newTarget };
        }
    },

    /**
     * Resetta il target di una settimana specifica al target generico.
     * Elimina l'override se esiste.
     */
    resetToDefault: (receiverId: string, year: number, weekNumber: number): boolean => {
        const index = targets.findIndex(
            (t) => t.receiverId === receiverId && t.year === year && t.weekNumber === weekNumber
        );
        if (index === -1) return false;

        targets.splice(index, 1);
        return true;
    },

    /**
     * Ottiene il target generico settimanale per un receiver in un anno
     */
    getDefaultTarget: (receiverId: string, year: number): ReceiverWeeklyTarget | null => {
        const genericTarget = targets.find(
            (t) => t.receiverId === receiverId && t.year === year && t.weekNumber === undefined
        );
        return genericTarget ? { ...genericTarget } : null;
    },

    /**
     * Crea o aggiorna il target generico settimanale
     */
    setDefaultTarget: (receiverId: string, year: number, containerTarget: number, revenueTarget: number): ReceiverWeeklyTarget => {
        const existing = targets.find(
            (t) => t.receiverId === receiverId && t.year === year && t.weekNumber === undefined
        );

        if (existing) {
            // Aggiorna target esistente
            existing.containerTarget = containerTarget;
            existing.revenueTarget = revenueTarget;
            existing.updatedAt = new Date();
            return { ...existing };
        } else {
            // Crea nuovo target
            const newTarget: ReceiverWeeklyTarget = {
                id: `target-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                receiverId,
                year,
                // weekNumber undefined = target generico
                containerTarget,
                revenueTarget,
                createdAt: new Date(),
                updatedAt: new Date(),
            };
            targets.push(newTarget);
            return { ...newTarget };
        }
    },
};

